import { jsx as _jsx } from "react/jsx-runtime";
import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import { Button } from '@rocketlane/ui';
import { Result } from 'antd';
import React, { Suspense, useState } from 'react';
import { AccountAPI } from 'api/account';
import { PageLoading } from 'components/atoms/loading';
import { DesktopMagicLinkBounce } from 'components/molecules/desktop-magic-link-bounce';
import { MagicLinkGeneratePage } from 'components/molecules/session-setup';
import { TokenErrorStatus } from 'components/organisms/token-error/constant';
import { RoutePaths } from 'constant';
import { useLoginWithToken } from 'hooks/use-login-with-token';
import { useQueryParams } from 'hooks/use-query-params';
import { useTranslation } from 'hooks/use-translation';
import { useAccountMetaQuery } from 'queries';
import { TokenType } from 'services';
import { isNonBrowserMagicLinkAgent } from 'utils/magic-link-agent';
import { getDecodedEmail, getTokenMeta, isLinkMagicDecoded, isResendableLinkMagic } from 'utils/token';
const TokenError = /*#__PURE__*/ React.lazy(()=>import('components/organisms/token-error/token-error').then((d)=>({
            default: d.TokenError
        })));
export const SessionError = ()=>{
    return /*#__PURE__*/ _jsx(Result, {
        status: "error",
        title: useTranslation('session.error.title'),
        subTitle: useTranslation('session.error.message'),
        extra: [
            /*#__PURE__*/ _jsx("a", {
                href: RoutePaths.newMagicLink,
                children: /*#__PURE__*/ _jsx(Button, {
                    variant: "primary",
                    type: "button",
                    rightIcon: /*#__PURE__*/ _jsx(ArrowRight16, {}),
                    children: useTranslation('magic_link.new')
                })
            }, "new-magic-link")
        ]
    });
};
const MagicLinkConsume = ({ token, tokenType, agent })=>{
    var _ref;
    var _data_okta;
    const [isLoading, isError] = useLoginWithToken(token, tokenType);
    const { data } = useAccountMetaQuery();
    const cookiesEnabled = (_ref = data === null || data === void 0 ? void 0 : (_data_okta = data.okta) === null || _data_okta === void 0 ? void 0 : _data_okta.cookiesEnabled) !== null && _ref !== void 0 ? _ref : false;
    const { decoded, isExpired } = getTokenMeta(token);
    if (isLoading) {
        return /*#__PURE__*/ _jsx(PageLoading, {});
    }
    if (isError) {
        if (cookiesEnabled && isExpired && decoded && isResendableLinkMagic(decoded)) {
            return /*#__PURE__*/ _jsx(Suspense, {
                fallback: /*#__PURE__*/ _jsx(PageLoading, {}),
                children: /*#__PURE__*/ _jsx(TokenError, {
                    status: TokenErrorStatus.Expired,
                    email: getDecodedEmail(decoded),
                    resendEmail: ()=>AccountAPI.resendMagicLink(token, agent),
                    linkType: isLinkMagicDecoded(decoded) ? decoded.link_type : undefined
                })
            });
        }
        return /*#__PURE__*/ _jsx(MagicLinkGeneratePage, {});
    }
    return null;
};
export const MagicLinkPage = ()=>{
    var _params_get, _params_get1;
    const params = useQueryParams();
    const token = (_params_get = params.get('token')) !== null && _params_get !== void 0 ? _params_get : '';
    // In some cases customer will generate the token and pass it via url
    const extToken = (_params_get1 = params.get('rl-token')) !== null && _params_get1 !== void 0 ? _params_get1 : '';
    const agent = params.get('agent');
    const resolvedToken = token || extToken || '';
    const tokenType = extToken ? TokenType.External : TokenType.Rocketlane;
    const [continueInBrowser, setContinueInBrowser] = useState(false);
    // Non-browser agents: bounce to the desktop app — do not redeem on web
    // unless the custom scheme does not take over.
    if (resolvedToken && isNonBrowserMagicLinkAgent(agent) && !continueInBrowser) {
        return /*#__PURE__*/ _jsx(DesktopMagicLinkBounce, {
            token: resolvedToken,
            agent: agent !== null && agent !== void 0 ? agent : '',
            onContinueInBrowser: ()=>setContinueInBrowser(true)
        });
    }
    return /*#__PURE__*/ _jsx(MagicLinkConsume, {
        token: resolvedToken,
        tokenType: tokenType,
        agent: agent
    });
};
